import { del, get, set } from 'idb-keyval';

import { IPoolRawMeta } from 'modules/pool/types';

type CacheEntry<T = IPoolRawMeta> = {
  cid: string;
  data: T;
  timestamp: number;
};

export type PoolMetaFetcher<T = IPoolRawMeta> = (cid: string) => Promise<T>;

const CACHE_TTL = 1000 * 60 * 60 * 24 * 7; // 1 week

function makeKey(poolId: string) {
  return `ipfs-cache:${poolId}`;
}

function isIndexDBSupported(): boolean {
  return !!window?.indexedDB;
}

export async function getCached<T = IPoolRawMeta>(
  poolId: string,
): Promise<CacheEntry<T> | null> {
  const isSupported = isIndexDBSupported();
  if (!isSupported) {
    return null;
  }

  const entry = await get<CacheEntry<T>>(makeKey(poolId));
  if (!entry) return null;

  if (Date.now() - entry.timestamp > CACHE_TTL) {
    await del(makeKey(poolId));
    return null;
  }

  return entry;
}

export async function setCached<T = IPoolRawMeta>(
  poolId: string,
  cid: string,
  data: T,
): Promise<void> {
  const isSupported = isIndexDBSupported();
  if (!isSupported) {
    return;
  }

  await set(makeKey(poolId), { cid, data, timestamp: Date.now() });
}

export async function memoizePromiseStorage<T = IPoolRawMeta>(
  cid: string,
  fetcher: PoolMetaFetcher<T>,
): Promise<T> {
  const cached = await getCached<T>(cid);
  if (cached && cached.cid === cid) {
    return cached.data;
  }

  const data: T = await fetcher(cid);
  await setCached<T>(cid, cid, data);

  return data;
}

export async function memoizePoolPromiseStorage(
  poolId: string,
  cid: string,
  fetcher: PoolMetaFetcher,
): Promise<IPoolRawMeta> {
  const cached = await getCached(poolId);
  if (cached && cached.cid === cid) {
    return cached.data;
  }

  const data = await fetcher(cid);
  await setCached(poolId, cid, data);

  return data;
}
